import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  Icon,
  IconButton,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useExternalOverrideKeyTermsForIngestionFolderMutation } from '@client/graphql/__generated__/document-operations';
import {
  ExternalIngestionFolderFragment,
  ExternalIngestionFolderMlsListingFragment,
  KeyTermsSectionFragment,
  TransactionSide,
} from '@client/graphql/__generated__/types';
import { FieldType } from '@common/enums';
import { formatToCurrency } from '@common/utils/formatter';
import dayjs from 'dayjs';
import { BookUpIcon, PencilIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import {
  PropertyOption,
  useDarwinSync,
} from '~/apps/extension/pages/Compliance/context/hooks/useDarwinSync';
import { formatUnderscoreField } from '~/common/utils/formatter';
import { formatPercentage } from '~/common/utils/percentage';
import { Select } from '~/services/document/common/Select';
import { useAppContext } from '~/services/main/contexts/AppContext';
import { EditableKeyTermField } from './components/EditableKeyTermField';
import { DarwinUploadPopover } from './DarwinUploadPopover';
import {
  extractKeyTermValues,
  getDarwinSyncableParticipants,
  getGroupedKeyTermsToDisplay,
  getParticipantInfo,
} from './utils/ingestionDocuments.utils';

interface MLSInfoProps {
  mlsListing?: ExternalIngestionFolderMlsListingFragment;
}

const MLSInfo: FC<MLSInfoProps> = ({ mlsListing }) => {
  if (!mlsListing) return null;

  return (
    <>
      <Divider borderColor="compliance.divider" pt={1} />
      <VStack align="stretch" spacing={1}>
        <Flex align="center" gap={1} justify="space-between">
          <Text color="compliance.text.muted">Source: </Text>
          <Text>{mlsListing?.mls?.originatingSystemName}</Text>
        </Flex>
        <Flex align="center" gap={1} justify="space-between">
          <Text color="compliance.text.muted">MLS#: </Text>
          <Text>{mlsListing?.listingId}</Text>
        </Flex>
      </VStack>
    </>
  );
};

const formatCurrencyValue = (value?: number | null): string => {
  if (value === null || value === undefined) {
    return '—';
  }

  return formatToCurrency(value) ?? '—';
};

const formatDateValue = (value?: string | Date | null) => {
  if (!value) {
    return '—';
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format('MMM D, YYYY') : '—';
};

const formatKeyTermValueFromField = (
  value: unknown,
  fieldFormat?: {
    type: string;
    options?: { precision?: number; formatString?: string };
  }
): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (!fieldFormat) {
    return String(value);
  }

  switch (fieldFormat.type) {
    case 'CURRENCY': {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        return formatCurrencyValue(numValue) || '—';
      }
      break;
    }
    case 'DATE': {
      return formatDateValue(value as string | Date);
    }
    case 'NUMBER': {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        const precision =
          typeof fieldFormat?.options?.precision === 'number'
            ? fieldFormat.options.precision
            : undefined;

        return precision !== undefined
          ? numValue.toLocaleString(undefined, {
              minimumFractionDigits: precision,
              maximumFractionDigits: precision,
            })
          : numValue.toLocaleString();
      }
      break;
    }
    case 'PERCENTAGE': {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        const precision =
          typeof fieldFormat?.options?.precision === 'number'
            ? fieldFormat.options.precision
            : undefined;

        return formatPercentage(numValue, precision, {
          treatAsFraction: false,
        });
      }
      break;
    }
  }

  return String(value);
};

const toTitleCase = (value: string): string =>
  value
    .split(' ')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');

const formatTagValue = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const formatted = formatUnderscoreField(value);

  return toTitleCase(formatted);
};

interface KeyTermsCardProps {
  folder?: ExternalIngestionFolderFragment;
  mlsListing?: ExternalIngestionFolderMlsListingFragment;
  keyTerms?: KeyTermsSectionFragment[];
  variant?: 'internal' | 'external';
}

export const KeyTermsCard: FC<KeyTermsCardProps> = ({
  folder,
  mlsListing,
  keyTerms,
  variant = 'internal',
}) => {
  const toast = useToast();

  // Edit mode state
  const [editingTabIndex, setEditingTabIndex] = useState<number | null>(null);
  const [editedValues, setEditedValues] = useState<
    Record<string, string | number | boolean | null | undefined>
  >({});
  const [initialValues, setInitialValues] = useState<
    Record<string, string | number | boolean | null | undefined>
  >({});
  const [currentTabIndex, setCurrentTabIndex] = useState(0);

  // Mutation
  const [overrideKeyTerms, { loading: saving }] =
    useExternalOverrideKeyTermsForIngestionFolderMutation();

  const keyTermGroups = useMemo(() => {
    return getGroupedKeyTermsToDisplay(keyTerms);
  }, [keyTerms]);

  const keyTermValues = useMemo(() => {
    return extractKeyTermValues(keyTerms);
  }, [keyTerms]);

  const participantInfo = useMemo(() => {
    return getParticipantInfo(folder?.participants);
  }, [folder?.participants]);

  const darwinSyncableParticipants = useMemo(() => {
    return getDarwinSyncableParticipants(
      folder?.participants,
      folder?.transactionSide
    );
  }, [folder?.participants, folder?.transactionSide]);

  const { selectedOrganization } = useAppContext();
  const {
    isDarwinSyncEnabled,
    darwinProperties,
    darwinPropertyLoading,
    selectedProperty,
    setSelectedProperty,
    propertyOptions,
    handleDarwinSync,
  } = useDarwinSync({
    folder,
    selectedOrganizationId: selectedOrganization?.id,
  });

  // Edit mode handlers
  const handleEditClick = () => {
    const currentGroup = keyTermGroups[currentTabIndex];
    if (!currentGroup) return;

    // Initialize edited values from current field values
    // Only include editable fields
    const initialValues: Record<
      string,
      string | number | boolean | null | undefined
    > = {};

    currentGroup.fields.forEach(({ field }) => {
      // Skip uneditable fields
      if (!field.isEditable) {
        return;
      }

      let currentValue: string | number | boolean | null | undefined = field
        .values?.[0]?.value as string | number | boolean | null | undefined;

      if (
        field.fieldType === FieldType.NUMBER &&
        currentValue !== null &&
        currentValue !== undefined &&
        currentValue !== ''
      ) {
        const numericValue =
          typeof currentValue === 'number'
            ? currentValue
            : Number(currentValue);

        if (Number.isFinite(numericValue)) {
          currentValue = numericValue;
        }
      }

      initialValues[field.key] = currentValue ?? undefined;
    });

    setInitialValues(initialValues);
    setEditedValues(initialValues);
    setEditingTabIndex(currentTabIndex);
  };

  const handleCancelEdit = () => {
    setEditingTabIndex(null);
    setEditedValues({});
    setInitialValues({});
  };

  const handleSaveEdit = async () => {
    if (!folder?.id) return;

    const currentGroup = keyTermGroups[currentTabIndex];
    if (!currentGroup) return;

    // Create a set of editable field keys for validation
    const editableKeys = new Set(
      currentGroup.fields
        .filter(({ field }) => field.isEditable)
        .map(({ field }) => field.key)
    );

    try {
      // Convert edited values to mutation format
      // Only include values that have changed from their initial state
      // and are editable
      const keyTermOverrides = Object.entries(editedValues)
        .filter(([key, value]) => {
          // Exclude undefined values
          if (value === undefined) return false;

          // Only include if the field is editable
          if (!editableKeys.has(key)) return false;

          // Only include if the value has changed from initial
          const initialValue = initialValues[key];

          return value !== initialValue;
        })
        .map(([key, value]) => ({
          key,
          value,
        }));

      await overrideKeyTerms({
        variables: {
          input: {
            ingestionFolderUuid: folder.id,
            keyTermOverrides,
          },
        },
        refetchQueries: ['ExternalIngestionFolderListForOrganization'],
        awaitRefetchQueries: true,
      });

      toast({
        title: 'Key terms updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setEditingTabIndex(null);
      setEditedValues({});
      setInitialValues({});
    } catch (error) {
      toast({
        title: 'Failed to update key terms',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleFieldChange = (
    fieldMappingKey: string,
    value: string | number | boolean | null | undefined
  ) => {
    setEditedValues((prev) => ({
      ...prev,
      [fieldMappingKey]: value,
    }));
  };

  const isEditing = editingTabIndex === currentTabIndex;

  // Get the currently selected Darwin property based on selector
  const darwinProperty = useMemo(() => {
    if (!selectedProperty?.value || !darwinProperties) {
      return undefined;
    }

    return darwinProperties.find(
      (p) => p.propertyId.toString() === selectedProperty.value
    );
  }, [selectedProperty, darwinProperties]);

  const transactionSide = folder?.transactionSide ?? null;

  const darwinFields = useMemo(() => {
    const buyersAgents = darwinProperty?.participants?.buyersAgents ?? [];
    const listingAgents = darwinProperty?.participants?.listingAgents ?? [];
    const buyers = darwinProperty?.participants?.buyers ?? [];
    const sellers = darwinProperty?.participants?.sellers ?? [];

    const buyersAgentsValue =
      buyersAgents.length > 0
        ? buyersAgents.map((agent) => agent.personName).join(', ')
        : '—';

    const listingAgentsValue =
      listingAgents.length > 0
        ? listingAgents.map((agent) => agent.personName).join(', ')
        : '—';

    const buyersValue =
      buyers.length > 0
        ? buyers.map((participant) => participant.personName).join(', ')
        : '—';

    const sellersValue =
      sellers.length > 0
        ? sellers.map((participant) => participant.personName).join(', ')
        : '—';

    const isBuySide = transactionSide === TransactionSide.BUY;
    const isSellSide = transactionSide === TransactionSide.SELL;

    const formatCommissionRate = (value?: number | null): string =>
      value == null
        ? '—'
        : formatPercentage(value, undefined, { treatAsFraction: true });

    const fields: Array<{ label: string; value: string }> = [
      {
        label: 'Property Id',
        value: darwinProperty?.propertyId?.toString() ?? '—',
      },
      {
        label: 'Property address',
        value: darwinProperty?.propertyAddress || 'Not set',
      },
      {
        label: 'MLS number 1',
        value: darwinProperty?.mlsNumber1 ?? '—',
      },
      {
        label: 'Status',
        value: darwinProperty?.status ?? '—',
      },
      {
        label: 'Type code',
        value: darwinProperty?.typeCodeName ?? '—',
      },
      {
        label: 'Accounting code',
        value: darwinProperty?.classificationName ?? '—',
      },
      {
        label: 'Property type',
        value: darwinProperty?.propertyTypeName ?? '—',
      },
      {
        label: 'Listing price',
        value: formatCurrencyValue(darwinProperty?.listingPrice ?? null),
      },
      {
        label: 'List date',
        value: formatDateValue(
          (darwinProperty?.listDate as string | Date | null | undefined) ?? null
        ),
      },
      {
        label: 'Pending date',
        value: formatDateValue(
          (darwinProperty?.pendingDate as string | Date | null | undefined) ??
            null
        ),
      },
      {
        label: 'Selling price',
        value: formatCurrencyValue(darwinProperty?.sellingPrice ?? null),
      },
      {
        label: 'Commission price',
        value: formatCurrencyValue(darwinProperty?.commissionPrice ?? null),
      },
    ];

    if (isSellSide) {
      fields.push(
        {
          label: 'Listing Commission Rate',
          value: formatCommissionRate(darwinProperty?.listCommRate ?? null),
        },
        {
          label: 'Listing Commission Amount',
          value: formatCurrencyValue(darwinProperty?.listCommission ?? null),
        },
        {
          label: 'Co-broke Commission Rate',
          value: formatCommissionRate(darwinProperty?.cobrokeCommRate ?? null),
        },
        {
          label: 'Co-broke Commission Amount',
          value: formatCurrencyValue(darwinProperty?.cobrokeCommission ?? null),
        }
      );
    }

    if (isBuySide) {
      fields.push(
        {
          label: 'Selling Commission Rate',
          value: formatCommissionRate(darwinProperty?.sellCommRate ?? null),
        },
        {
          label: 'Selling Commission Amount',
          value: formatCurrencyValue(darwinProperty?.sellCommission ?? null),
        }
      );
    }

    fields.push(
      {
        label: 'Est. close date',
        value: formatDateValue(
          (darwinProperty?.estimatedClosingDate as
            | string
            | Date
            | null
            | undefined) ?? null
        ),
      },
      {
        label: 'Buyers Agents',
        value: buyersAgentsValue,
      },
      {
        label: 'Listing Agents',
        value: listingAgentsValue,
      },
      {
        label: 'Buyers',
        value: buyersValue,
      },
      {
        label: 'Sellers',
        value: sellersValue,
      }
    );

    return fields;
  }, [darwinProperty, transactionSide]);

  const folderName = folder?.folderName?.trim();

  const tagValues = useMemo(() => {
    return [
      formatTagValue(folder?.status),
      formatTagValue(folder?.transactionSide),
      formatTagValue(folder?.transactionPipeline?.transactionPropertyType),
    ].filter(Boolean) as string[];
  }, [
    folder?.status,
    folder?.transactionSide,
    folder?.transactionPipeline?.transactionPropertyType,
  ]);

  return (
    <Card>
      <CardHeader letterSpacing="0" p={3}>
        <Flex align="flex-start" gap={4} justify="space-between">
          <Flex direction="column" flex="1" gap={2} minW={0}>
            {folderName && (
              <Text
                color="compliance.text.primary"
                fontSize="md"
                fontWeight="medium"
                noOfLines={1}
                textTransform="none"
              >
                {folderName}
              </Text>
            )}
            {tagValues.length > 0 && (
              <Flex align="center" flexWrap="wrap" gap={2}>
                {tagValues.map((tag) => (
                  <Tag
                    key={tag}
                    bg="compliance.tag.bg"
                    borderRadius="xs"
                    color="compliance.tag.text"
                    px="6px"
                    py="2px"
                    size="sm"
                    textTransform="none"
                  >
                    <Text fontSize="sm" fontWeight="normal">
                      {tag}
                    </Text>
                  </Tag>
                ))}
              </Flex>
            )}
          </Flex>
          <Flex align="center" gap={1}>
            {!isEditing &&
              currentTabIndex < keyTermGroups.length &&
              variant === 'external' && (
                <IconButton
                  aria-label="edit-key-terms"
                  color="compliance.text.muted"
                  height={6}
                  icon={<Icon as={PencilIcon} size="sm" />}
                  minW={6}
                  p={2}
                  size="sm"
                  variant="ghost"
                  onClick={handleEditClick}
                />
              )}
            {!isEditing && isDarwinSyncEnabled && variant === 'external' && (
              <DarwinUploadPopover
                darwinProperties={darwinProperties}
                darwinPropertyLoading={darwinPropertyLoading}
                keyTermValues={keyTermValues}
                participants={darwinSyncableParticipants}
                propertyOptions={propertyOptions}
                selectedProperty={selectedProperty}
                transactionSide={transactionSide}
                onPropertyChange={setSelectedProperty}
                onUpload={handleDarwinSync}
              >
                <IconButton
                  aria-label="write-key-terms"
                  boxSize={6}
                  icon={<Icon as={BookUpIcon} size="sm" />}
                  p={2}
                  size="sm"
                  variant="ghost"
                />
              </DarwinUploadPopover>
            )}
            {isEditing && variant === 'external' && (
              <>
                <Button
                  borderColor="compliance.border.divider"
                  color="compliance.text.muted"
                  height={6}
                  isDisabled={saving}
                  size="xs"
                  variant="outline"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  _hover={{
                    bgColor: 'whiteAlpha.700',
                  }}
                  bgColor="white"
                  color="indigo.900"
                  height={6}
                  isLoading={saving}
                  size="xs"
                  onClick={() => {
                    void handleSaveEdit();
                  }}
                >
                  Save
                </Button>
              </>
            )}
          </Flex>
        </Flex>
      </CardHeader>
      <CardBody pb={0} pt={3} px={0}>
        <Tabs
          index={currentTabIndex}
          size="sm"
          onChange={(index) => {
            if (isEditing) return; // Prevent tab switching while editing
            setCurrentTabIndex(index);
          }}
        >
          <TabList overflowX="auto">
            {keyTermGroups.map((group, index) => (
              <Tab
                key={`${group.title}-${index}`}
                isDisabled={isEditing && index !== currentTabIndex}
                whiteSpace="nowrap"
              >
                {group.title || 'Key Terms'}
              </Tab>
            ))}

            {participantInfo.length > 0 && (
              <Tab isDisabled={isEditing} whiteSpace="nowrap">
                Participants
              </Tab>
            )}
            {isDarwinSyncEnabled && (
              <Tab isDisabled={isEditing} whiteSpace="nowrap">
                Darwin Property
              </Tab>
            )}
          </TabList>

          <TabPanels>
            {/* Key Terms Tabs */}
            {keyTermGroups.map((group, groupIndex) => (
              <TabPanel key={`${group.title}-${groupIndex}`} p={3}>
                <VStack align="stretch" spacing={1}>
                  {group.fields.map(({ label, value, field }, fieldIndex) => {
                    const editValue:
                      | string
                      | number
                      | boolean
                      | null
                      | undefined =
                      editedValues[field.key] ??
                      (field.values?.[0]?.value as
                        | string
                        | number
                        | boolean
                        | null
                        | undefined);

                    const hasValuesSources =
                      field.values && field.values.length > 0;
                    const tooltipContent = hasValuesSources ? (
                      <VStack align="stretch" spacing={1}>
                        {field.values.map((valueObj, idx) => (
                          <Flex
                            key={idx}
                            align="center"
                            gap={4}
                            justify="space-between"
                          >
                            <Text fontSize="sm" textAlign="left">
                              Source: {valueObj.sourceLabel || 'Unknown source'}
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {formatKeyTermValueFromField(
                                valueObj.value,
                                field.fieldFormat ?? undefined
                              )}
                            </Text>
                          </Flex>
                        ))}
                      </VStack>
                    ) : null;

                    return (
                      <Flex
                        key={fieldIndex}
                        align="center"
                        justify="space-between"
                      >
                        <Text color="compliance.text.muted" flex="1" mr={2}>
                          {label}:
                        </Text>
                        {isEditing && groupIndex === editingTabIndex ? (
                          !field.isEditable ? (
                            <Tooltip
                              bg="compliance.tooltip.bg"
                              borderColor="compliance.tooltip.border"
                              borderWidth="1px"
                              color="compliance.tooltip.text"
                              label="Editing this key term is coming soon"
                              placement="top"
                            >
                              <Text
                                color="compliance.text.muted"
                                fontWeight="medium"
                                textAlign="right"
                              >
                                {value}
                              </Text>
                            </Tooltip>
                          ) : (
                            <Box flex="1" maxW="35%">
                              <EditableKeyTermField
                                field={field}
                                isDisabled={saving}
                                size="sm"
                                value={editValue}
                                onChange={(value) =>
                                  handleFieldChange(field.key, value)
                                }
                              />
                            </Box>
                          )
                        ) : (
                          <Tooltip
                            bg="compliance.tooltip.bg"
                            borderColor="compliance.tooltip.border"
                            borderWidth="1px"
                            color="compliance.tooltip.text"
                            isDisabled={!hasValuesSources}
                            label={tooltipContent}
                            placement="top"
                          >
                            <Text fontWeight="medium" textAlign="right">
                              {value}
                            </Text>
                          </Tooltip>
                        )}
                      </Flex>
                    );
                  })}

                  {/* MLS Information for first tab */}
                  {groupIndex === 0 && <MLSInfo mlsListing={mlsListing} />}
                </VStack>
              </TabPanel>
            ))}

            {/* Participants Tab */}
            {participantInfo.length > 0 && (
              <TabPanel p={3}>
                <VStack align="stretch" spacing={1}>
                  {participantInfo.map(({ label, value }, index) => (
                    <Flex key={`participant-${index}`} justify="space-between">
                      <Text color="compliance.text.muted" flex="1" mr={2}>
                        {label}:
                      </Text>
                      <Text fontWeight="medium" textAlign="right">
                        {value}
                      </Text>
                    </Flex>
                  ))}
                </VStack>
              </TabPanel>
            )}

            {isDarwinSyncEnabled && (
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  {darwinPropertyLoading ? (
                    <Flex align="center" justify="center" minH="120px">
                      <Spinner size="sm" />
                    </Flex>
                  ) : (
                    <>
                      <Select<PropertyOption>
                        isDisabled={propertyOptions.length === 0}
                        options={propertyOptions}
                        placeholder="Choose a property..."
                        value={selectedProperty}
                        onChange={(option: PropertyOption | null) =>
                          setSelectedProperty(option)
                        }
                      />

                      {!darwinPropertyLoading && darwinProperty ? (
                        <VStack align="stretch" spacing={1}>
                          {darwinFields.map(({ label, value }) => (
                            <Flex key={label} justify="space-between">
                              <Text
                                color="compliance.text.muted"
                                flex="1"
                                mr={2}
                              >
                                {label}:
                              </Text>
                              <Text fontWeight="medium" textAlign="right">
                                {value}
                              </Text>
                            </Flex>
                          ))}
                        </VStack>
                      ) : (
                        <Text color="compliance.text.muted" fontSize="sm">
                          No Darwin property data yet. Write to Darwin to sync
                          these key terms.
                        </Text>
                      )}
                    </>
                  )}
                </VStack>
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      </CardBody>
    </Card>
  );
};
